import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { requestKey, type RequestItem } from "./use-purchase-requests";

export type ProcessingLine = {
  id: string; requestId: string; prNumber: string; locationId: string; locationName: string;
  itemId: string; itemName: string; itemNameAr: string | null; quantity: string; unit: string;
  neededByDate: string | null; submittedAt: string; fallbackHours: number | null; teamRoutedAt: string | null;
  routingMode: string; recipients: { id: string; name: string }[]; toMe: boolean;
  supplierId: string | null; supplierName: string | null; unitCost: string | null;
};
export type ProcessingData = {
  lines: ProcessingLine[];
  counts: { mine: number; all: number; linesOpen: number; suppliers: number; overdueLines: number; pendingApproval: number };
  suppliers: { id: string; name: string; nameAr: string | null }[];
};
export type ProcessingGroup = {
  supplierId: string; pricingSource: "catalog" | "supplier_quote" | "manual"; notes: string;
  lines: { requestLineId: string; itemId: string; quantity: string; unit: string; unitCost: string; deliveryDate: string; changeReason?: string }[];
};
export const processingKey = (workspaceId: string) => ["/api/purchase-request-processing", workspaceId] as const;
export async function processingApi<T>(workspaceId: string, path = "", method = "GET", body?: unknown): Promise<T> {
  return (await apiRequest(method, `/api/purchase-request-processing${path}${path.includes("?") ? "&" : "?"}workspaceId=${encodeURIComponent(workspaceId)}`, body)).json();
}
export function useRequestProcessing(workspaceId: string, enabled: boolean) {
  return useQuery<ProcessingData>({ queryKey: processingKey(workspaceId), queryFn: () => processingApi(workspaceId), enabled: !!workspaceId && enabled, refetchInterval: 60000 });
}
export function useProcessingCount(workspaceId: string, enabled: boolean) {
  return useQuery<{ count: number }>({ queryKey: [...processingKey(workspaceId), "count"], queryFn: () => processingApi(workspaceId, "/count"), enabled: !!workspaceId && enabled, refetchInterval: 60000 });
}
export function useProcessingItems(workspaceId: string, locationId: string, supplierId: string, query: string, enabled: boolean) {
  const params = new URLSearchParams({ locationId, supplierId, q: query });
  return useQuery<(RequestItem & { unitCost: string | null })[]>({
    queryKey: [...processingKey(workspaceId), "items", locationId, supplierId, query],
    queryFn: () => processingApi(workspaceId, `/items?${params}`), enabled: !!workspaceId && !!locationId && !!supplierId && enabled,
  });
}
export function useProcessingActions(workspaceId: string) {
  const client = useQueryClient();
  const refresh = () => {
    void client.invalidateQueries({ queryKey: processingKey(workspaceId) });
    void client.invalidateQueries({ queryKey: requestKey(workspaceId) });
    void client.invalidateQueries({ queryKey: ["/api/purchase-orders"] });
  };
  const decline = useMutation({
    mutationFn: (body: { lineId: string; reason: string; note?: string }) => processingApi<{ ok: true }>(workspaceId, "/decline", "POST", body),
    onSuccess: refresh,
  });
  const create = useMutation({
    mutationFn: (body: { clientRequestId: string; groups: ProcessingGroup[] }) =>
      processingApi<{ purchaseOrders: { id: string; poNumber: string }[]; replayed: boolean }>(workspaceId, "/create-pos", "POST", body),
    onSuccess: refresh,
  });
  return { decline, create };
}
